import { LANGUAGE_PREFERENCES_STORAGE_KEY, LANGUAGE_PREFERENCES_VERSION } from './constants';
import { DEFAULT_LANGUAGE_PREFERENCES, type LanguageCode, type LanguagePreferences } from './types';

const isLanguageCode = (value: unknown): value is LanguageCode => {
  return value === 'en' || value === 'id';
};

const normalizeLanguagePreferences = (value: LanguagePreferences): LanguagePreferences => ({
  languageCode: isLanguageCode(value.languageCode)
    ? value.languageCode
    : DEFAULT_LANGUAGE_PREFERENCES.languageCode,
  version: typeof value.version === 'number' ? value.version : LANGUAGE_PREFERENCES_VERSION,
  updatedAt: value.updatedAt,
});

const isLanguagePreferences = (value: unknown): value is LanguagePreferences => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const candidate = value as Partial<LanguagePreferences>;
  return isLanguageCode(candidate.languageCode);
};

export const readLanguagePreferencesFromStorage = (): LanguagePreferences => {
  try {
    const raw = localStorage.getItem(LANGUAGE_PREFERENCES_STORAGE_KEY);
    if (!raw) {
      return DEFAULT_LANGUAGE_PREFERENCES;
    }
    const parsed = JSON.parse(raw) as unknown;
    if (!isLanguagePreferences(parsed)) {
      return DEFAULT_LANGUAGE_PREFERENCES;
    }
    return normalizeLanguagePreferences(parsed);
  } catch {
    return DEFAULT_LANGUAGE_PREFERENCES;
  }
};

export const writeLanguagePreferencesToStorage = (value: LanguagePreferences): LanguagePreferences => {
  const normalized: LanguagePreferences = {
    ...normalizeLanguagePreferences(value),
    updatedAt: value.updatedAt ?? new Date().toISOString(),
  };
  localStorage.setItem(LANGUAGE_PREFERENCES_STORAGE_KEY, JSON.stringify(normalized));
  return normalized;
};
